/**
 * Backend API client.
 *
 * Token storage is localStorage. Device token (long-lived, 30d) goes in
 * `nt.deviceToken`. User tokens (access 15m, refresh 30d) go in
 * `nt.userAccessToken` and `nt.userRefreshToken`.
 *
 * On a 401 from a user-access call, the client transparently attempts
 * to refresh via POST /v1/users/refresh. If that succeeds, it retries
 * the original request once. If it fails, the user tokens are cleared
 * and the caller gets the 401.
 */

// v0.9.695: env-overridable so a Netlify branch deploy (sandbox) can point
// at a Cloud Run tagged revision while production stays on the default.
// v0.9.1521: optional-chained so lib modules import cleanly under plain
// Node (tests, tooling) where import.meta.env does not exist. Vite still
// statically replaces import.meta.env at build time, so the app path is
// unchanged.
/**
 * v0.9.1730: the fallback points at RENDER, not Cloud Run.
 *
 * The old default was a Cloud Run service in a Google project whose owning
 * account became unreachable. The project is now being wound down - the
 * storage bucket started refusing reads, and the API service itself has
 * since disappeared, so every build that fell back to this constant was
 * pointing the whole app at a host that no longer exists.
 *
 * VITE_BACKEND_URL still wins, so a deploy can aim anywhere. But the
 * fallback is the value that gets used when nobody sets the variable, and
 * it must therefore be somewhere that actually answers. Leaving a dead host
 * as the default is how a build silently takes the product down.
 */
const BACKEND_URL =
  ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BACKEND_URL) ||
  'https://nexttab-backend.onrender.com';
// v0.9.228: exported so binary endpoints (report export downloads) can build
// their own fetch with the same base URL without duplicating the constant.
export { BACKEND_URL };


const TOKEN_KEYS = {
  device: 'nt.deviceToken',
  // v0.9.959: the durable device pairing key. Persisted so the app can re-mint
  // an expired device token on its own via /v1/devices/auth - a terminal can sit
  // cold for any length of time (banquet room used every 6 weeks, seasonal
  // station) and still work instantly on power-up, with no re-pair. Revocation
  // (devices.revokedAt, checked on every request) remains the kill switch.
  deviceKey: 'nt.deviceKey',
  userAccess: 'nt.userAccessToken',
  userRefresh: 'nt.userRefreshToken',
  tenantId: 'nt.tenantId',
  deviceId: 'nt.deviceId',
  userId: 'nt.userId',
} as const;

/**
 * Storage routing.
 *
 * Device-level state (the device JWT, the tenant the device belongs to,
 * the device's own UUID) lives in localStorage so the device stays
 * paired across tab close/reopen and OS reboot.
 *
 * User-session state (access token, refresh token, user id) lives in
 * sessionStorage. sessionStorage is wiped when the tab closes, so
 * closing the tab forces a re-PIN on reopen - matching the operator's
 * mental model that "closing the POS logs me out" regardless of the
 * idle-logout timer setting. Inactivity within a single tab is still
 * handled by the idle timer in useIdleLogout.
 *
 * Migration: previous versions stored user tokens in localStorage. We
 * sweep those keys out at module init below so an existing operator
 * doesn't see a stale session resurface after upgrade.
 */
const USER_SESSION_KEYS: ReadonlySet<string> = new Set([
  TOKEN_KEYS.userAccess,
  TOKEN_KEYS.userRefresh,
  TOKEN_KEYS.userId,
]);

function storeFor(key: string): Storage {
  return USER_SESSION_KEYS.has(key) ? sessionStorage : localStorage;
}

// One-time migration on module load: clear any user-session keys that
// linger in localStorage from pre-v0.9.44 builds. Without this, an
// already-signed-in operator upgrading to v0.9.44+ would have their
// old localStorage user JWT re-hydrated on the next page load - the
// very thing this change is meant to prevent.
(function migrateUserSessionStorage() {
  try {
    for (const key of USER_SESSION_KEYS) {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
      }
    }
    // nt.userInfo also moved - cleared by auth.ts's own migration.
  } catch (_) { /* private mode etc - ignore */ }
})();

export type TokenKind = 'device' | 'user-access' | 'user-refresh';

export type ApiErrorBody = {
  error?: string;
  code?: string;
  details?: unknown;
  [k: string]: unknown;
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
    message?: string,
  ) {
    // v0.9.1070: prefer a `detail` message when the server provides one (e.g.
    // report handlers return { error: 'X_FAILED', detail: '<real cause>' }), so
    // the UI shows the actionable cause instead of just the error code.
    // v0.9.1469: `message` too - the portal event door and login return
    // human sentences there ("changed on another device since you loaded
    // it"), which beat the bare code ("stale") on a Save button.
    super(message ?? (typeof body?.detail === 'string' ? body.detail : undefined)
      ?? (typeof (body as { message?: unknown })?.message === 'string' ? (body as { message: string }).message : undefined)
      ?? body?.error ?? 'HTTP ' + status);
    this.name = 'ApiError';
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

export const tokens = {
  getDevice(): string | null { return storeFor(TOKEN_KEYS.device).getItem(TOKEN_KEYS.device); },
  setDevice(token: string | null): void {
    const s = storeFor(TOKEN_KEYS.device);
    if (token) s.setItem(TOKEN_KEYS.device, token);
    else s.removeItem(TOKEN_KEYS.device);
  },
  // v0.9.959: durable device pairing key (see TOKEN_KEYS.deviceKey).
  getDeviceKey(): string | null { return storeFor(TOKEN_KEYS.deviceKey).getItem(TOKEN_KEYS.deviceKey); },
  setDeviceKey(key: string | null): void {
    const s = storeFor(TOKEN_KEYS.deviceKey);
    if (key) s.setItem(TOKEN_KEYS.deviceKey, key);
    else s.removeItem(TOKEN_KEYS.deviceKey);
  },
  getUserAccess(): string | null { return storeFor(TOKEN_KEYS.userAccess).getItem(TOKEN_KEYS.userAccess); },
  setUserAccess(token: string | null): void {
    const s = storeFor(TOKEN_KEYS.userAccess);
    if (token) s.setItem(TOKEN_KEYS.userAccess, token);
    else s.removeItem(TOKEN_KEYS.userAccess);
  },
  getUserRefresh(): string | null { return storeFor(TOKEN_KEYS.userRefresh).getItem(TOKEN_KEYS.userRefresh); },
  setUserRefresh(token: string | null): void {
    const s = storeFor(TOKEN_KEYS.userRefresh);
    if (token) s.setItem(TOKEN_KEYS.userRefresh, token);
    else s.removeItem(TOKEN_KEYS.userRefresh);
  },
  getTenantId(): string | null { return storeFor(TOKEN_KEYS.tenantId).getItem(TOKEN_KEYS.tenantId); },
  setTenantId(id: string | null): void {
    const s = storeFor(TOKEN_KEYS.tenantId);
    if (id) s.setItem(TOKEN_KEYS.tenantId, id);
    else s.removeItem(TOKEN_KEYS.tenantId);
  },
  getDeviceId(): string | null { return storeFor(TOKEN_KEYS.deviceId).getItem(TOKEN_KEYS.deviceId); },
  setDeviceId(id: string | null): void {
    const s = storeFor(TOKEN_KEYS.deviceId);
    if (id) s.setItem(TOKEN_KEYS.deviceId, id);
    else s.removeItem(TOKEN_KEYS.deviceId);
  },
  getUserId(): string | null { return storeFor(TOKEN_KEYS.userId).getItem(TOKEN_KEYS.userId); },
  setUserId(id: string | null): void {
    const s = storeFor(TOKEN_KEYS.userId);
    if (id) s.setItem(TOKEN_KEYS.userId, id);
    else s.removeItem(TOKEN_KEYS.userId);
  },
  clearAll(): void {
    for (const k of Object.values(TOKEN_KEYS)) {
      storeFor(k).removeItem(k);
    }
  },
};

export type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: TokenKind | 'none';
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * v0.9.1375: this context has no paired device.
 *
 * The manager suite runs in a browser that was never paired - no device
 * token, and no way to get one. Any call that does not name its auth mode
 * defaults to 'device', which in the portal means an Authorization header
 * that is simply absent, and the server answers "missing token". That is
 * how a fully hydrated menu panel ended up showing an auth error next to
 * data it had already loaded.
 *
 * Chasing every call site would fix today's panel and break the next one
 * the same way, so the fallback lives here. It is narrow: it only applies
 * where there is no device token to use, it only substitutes a token the
 * user already holds, and it grants nothing - every endpoint still decides
 * for itself. A route that genuinely needs a device now answers with a
 * clear 403 about permissions instead of a misleading 401 about a missing
 * header.
 */
let noDeviceContext = false;
export function setNoDeviceContext(v: boolean): void { noDeviceContext = v; }
export function isNoDeviceContext(): boolean { return noDeviceContext; }

function resolveToken(auth: TokenKind | 'none'): string | null {
  switch (auth) {
    case 'device': {
      const d = tokens.getDevice();
      if (d) return d;
      return noDeviceContext ? tokens.getUserAccess() : null;
    }
    case 'user-access': return tokens.getUserAccess();
    case 'user-refresh': return tokens.getUserRefresh();
    case 'none': return null;
  }
}

/**
 * Listeners for user-token loss (i.e. refresh failed; user must sign in again).
 * The auth module registers a listener to clear in-memory user state.
 */
type AuthLossListener = () => void;
const authLossListeners = new Set<AuthLossListener>();
export function onUserAuthLost(fn: AuthLossListener): () => void {
  authLossListeners.add(fn);
  return () => { authLossListeners.delete(fn); };
}

function notifyAuthLost(): void {
  tokens.setUserAccess(null);
  tokens.setUserRefresh(null);
  tokens.setUserId(null);
  for (const fn of authLossListeners) {
    try { fn(); } catch (_) { /* ignore */ }
  }
}

/**
 * v0.9.308: listeners for DEVICE revocation. When the backend reports the
 * device's registration was revoked, we clear the device tokens and notify
 * (App flips to the registration screen so a new code can be entered).
 */
type DeviceRevokedListener = () => void;
const deviceRevokedListeners = new Set<DeviceRevokedListener>();
export function onDeviceRevoked(fn: DeviceRevokedListener): () => void {
  deviceRevokedListeners.add(fn);
  return () => { deviceRevokedListeners.delete(fn); };
}

let deviceRevokedFired = false;
export function notifyDeviceRevoked(): void {
  // De-dupe: a revoked device can fire many failing requests at once.
  if (deviceRevokedFired) return;
  deviceRevokedFired = true;
  tokens.setDevice(null);
  tokens.setDeviceId(null);
  for (const fn of deviceRevokedListeners) {
    try { fn(); } catch (_) { /* ignore */ }
  }
}

/** Internal: refresh user-access using the stored refresh token. */
async function tryRefreshUserToken(): Promise<boolean> {
  const refresh = tokens.getUserRefresh();
  if (!refresh) return false;
  try {
    // v0.9.2008: in the Manager Suite (no paired device) renew through the
    // portal endpoint - it re-checks portal access, and it is the only one
    // that accepts a NextTab support session's refresh token.
    const res = await fetch(BACKEND_URL + (noDeviceContext ? '/v1/portal/refresh' : '/v1/users/refresh'), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify({ refreshToken: refresh }),
    });
    if (!res.ok) return false;
    const text = await res.text();
    if (!text) return false;
    const data = JSON.parse(text) as { accessToken?: string };
    if (typeof data.accessToken === 'string') {
      tokens.setUserAccess(data.accessToken);
      return true;
    }
    return false;
  } catch (_) {
    return false;
  }
}

// v0.9.959: re-mint an expired/invalid DEVICE token from the stored pairing key.
// This is what lets a terminal that's been cold for weeks/months work instantly
// on power-up: when a device call gets 401 "invalid token", we exchange the
// durable deviceKey for a fresh 30-day token and retry. A single shared promise
// dedups concurrent re-auths (on wake, many calls 401 at once - we want ONE
// /v1/devices/auth, not a stampede). Returns true if a fresh token was stored.
let deviceReauthInFlight: Promise<boolean> | null = null;

export async function tryReauthDeviceToken(): Promise<boolean> {
  if (deviceReauthInFlight) return deviceReauthInFlight;
  deviceReauthInFlight = (async () => {
    const key = tokens.getDeviceKey();
    if (!key) return false; // no stored key (older pairing) - can't self-heal
    try {
      const res = await fetch(BACKEND_URL + '/v1/devices/auth', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ deviceKey: key }),
      });
      if (!res.ok) {
        // 401/403 here means the key itself is bad (revoked or wrong) - genuine
        // re-pair territory, not something a retry fixes.
        return false;
      }
      const text = await res.text();
      if (!text) return false;
      const data = JSON.parse(text) as { token?: string };
      if (typeof data.token === 'string' && data.token.split('.').length === 3) {
        tokens.setDevice(data.token);
        return true;
      }
      return false;
    } catch (_) {
      return false;
    } finally {
      // cleared after the IIFE resolves (below)
    }
  })();
  try {
    return await deviceReauthInFlight;
  } finally {
    deviceReauthInFlight = null;
  }
}

async function doFetch(path: string, method: string, headers: Record<string, string>, body: unknown, signal: AbortSignal | undefined): Promise<Response> {
  return fetch(BACKEND_URL + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (text.length === 0) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const auth = opts.auth ?? 'device';

  async function buildAndFetch(): Promise<Response> {
    const headers: Record<string, string> = {
      accept: 'application/json',
      ...opts.headers,
    };
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    const token = resolveToken(auth);
    if (token) headers.authorization = 'Bearer ' + token;
    return doFetch(path, method, headers, opts.body, opts.signal);
  }

  let res: Response;
  try {
    res = await buildAndFetch();
  } catch (err) {
    throw new ApiError(0, { error: (err as Error).message }, 'network error');
  }

  // 401 on user-access: try one refresh + retry.
  if (res.status === 401 && auth === 'user-access') {
    const refreshed = await tryRefreshUserToken();
    if (refreshed) {
      try {
        res = await buildAndFetch();
      } catch (err) {
        throw new ApiError(0, { error: (err as Error).message }, 'network error');
      }
    } else {
      notifyAuthLost();
    }
  }

  // v0.9.959: 401 on a device call -> the device token expired/invalid. Re-mint
  // it from the stored pairing key and retry once. This is the cold-terminal
  // safety net: a station dark for weeks comes back to life on its first call
  // without a re-pair. If re-auth fails (no stored key, or key revoked), fall
  // through and surface the 401 as before (the device genuinely needs re-pair).
  // v0.9.1375: no device token means nothing to refresh - attempting it
  // would trade a clear error for a confusing re-pair prompt.
  if (res.status === 401 && auth === 'device' && !noDeviceContext) {
    const reauthed = await tryReauthDeviceToken();
    if (reauthed) {
      try {
        res = await buildAndFetch();
      } catch (err) {
        throw new ApiError(0, { error: (err as Error).message }, 'network error');
      }
    }
  }

  const parsed = await readBody(res);

  // v0.9.958: rolling device token. When the backend decides our device token is
  // nearing expiry it returns a fresh one in X-Refreshed-Device-Token. Swap it
  // into storage so an ACTIVE device's JWT slides forward without ever expiring.
  // (Complements the v0.9.959 cold-terminal re-auth above: rolling keeps active
  // devices fresh; deviceKey re-auth recovers ones that already lapsed.)
  if (auth === 'device') {
    const fresh = res.headers.get('X-Refreshed-Device-Token');
    if (fresh && fresh.split('.').length === 3 && fresh !== tokens.getDevice()) {
      tokens.setDevice(fresh);
    }
  }
  if (!res.ok) {
    // v0.9.308: device registration revoked -> clear it and surface to App.
    if (res.status === 403 && (parsed as ApiErrorBody)?.error === 'DEVICE_REVOKED') {
      notifyDeviceRevoked();
    }
    throw new ApiError(res.status, parsed as ApiErrorBody);
  }
  return parsed as T;
}

export function streamUrl(): string {
  const wsBase = BACKEND_URL.replace(/^https/, 'wss');
  const token = tokens.getDevice();
  if (!token) throw new Error('streamUrl: no device token stored');
  return wsBase + '/v1/stream?token=' + encodeURIComponent(token);
}

export function isDeviceProvisioned(): boolean {
  return !!tokens.getDevice() && !!tokens.getDeviceId();
}

export function isUserSignedIn(): boolean {
  return !!tokens.getUserAccess();
}

/**
 * v0.9.1804: upload a video for a board's video column. Raw bytes, not JSON -
 * videos are far past the JSON size limit. The server checks the format and
 * refuses HEVC (most TVs cannot play it) with a message meant for the owner,
 * which is surfaced as the thrown error.
 */
export async function uploadBoardVideo(file: File): Promise<{ url: string; rotation: number; posterUrl?: string }> {
  const token = resolveToken('user-access');
  const type = file.type === 'video/quicktime' || file.type === 'video/webm' ? file.type : 'video/mp4';
  const res = await fetch(BACKEND_URL + '/v1/boards/video', {
    method: 'POST',
    headers: { 'content-type': type, ...(token ? { authorization: 'Bearer ' + token } : {}) },
    body: file,
  });
  const data = await res.json().catch(() => ({})) as { url?: string; error?: string; rotation?: number; posterUrl?: string };
  if (!res.ok || !data.url) throw new Error(data.error || ('Upload failed (' + res.status + ')'));
  // v0.9.1805: the clockwise turn recorded in the file (backend 1310+; 0 before).
  // v0.9.1806: backend 1312 converts the clip and returns a poster frame.
  return { url: data.url, rotation: Number(data.rotation) || 0, posterUrl: data.posterUrl || undefined };
}

/**
 * v0.9.2016: attach a photo to a support ticket. Raw JPEG bytes (the caller
 * resizes first - see SupportTicketsPanel), signed-in user token, one retry
 * after a token refresh.
 */
export async function uploadSupportPhoto(ticketId: string, jpeg: Blob): Promise<void> {
  const send = () => {
    const token = resolveToken('user-access');
    return fetch(BACKEND_URL + '/v1/support/tickets/' + encodeURIComponent(ticketId) + '/photos', {
      method: 'POST',
      headers: { 'content-type': 'image/jpeg', ...(token ? { authorization: 'Bearer ' + token } : {}) },
      body: jpeg,
    });
  };
  let res = await send();
  if (res.status === 401) {
    if (await tryRefreshUserToken()) res = await send();
    else notifyAuthLost();
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error || ('Photo upload failed (' + res.status + ')'));
  }
}
